import {
  ColumnDef,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  Table,
  Row,
  Column,
  OnChangeFn,
  RowSelectionState,
  ColumnOrderState,
  ColumnSizingState,
  VisibilityState,
  PaginationState,
  SortingState,
  ColumnFiltersState,
} from '@tanstack/react-table';
import type { RTablePaginationProps } from '../RTable.props';

interface UseTableInstanceProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  enableRowSelection: boolean | ((row: Row<T>) => boolean);
  enableColumnFilters: boolean;
  rowSelection: RowSelectionState;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
  onPaginationChange?: (pagination?: RTablePaginationProps) => void;
  getRowCanExpand: (row: Row<T>) => boolean;
  /** Есть ли expandable rows — для управления columnOrder */
  hasExpandableRows: boolean;
  getRowId: (originalRow: T, index: number, parent?: Row<T>) => string;
  onUpdateTableData: (props: { row: Row<T>; column: Column<T, unknown>; value: unknown }) => void;
  skipAutoResetPageIndex: () => void;

  // State из контроллера
  columnOrder: ColumnOrderState;
  columnSizing: ColumnSizingState;
  columnVisibility: VisibilityState;
  paginationState: PaginationState;
  sorting: SortingState;
  columnFilters: ColumnFiltersState;

  // Setters из контроллера
  setColumnOrder: OnChangeFn<ColumnOrderState>;
  setColumnSizing: OnChangeFn<ColumnSizingState>;
  setColumnVisibility: OnChangeFn<VisibilityState>;
  setPaginationState: OnChangeFn<PaginationState>;
  setSorting: OnChangeFn<SortingState>;
  setColumnFilters: OnChangeFn<ColumnFiltersState>;
}

/**
 * Default column configuration for all table columns
 */
function getDefaultColumn<T>(): Partial<ColumnDef<T>> {
  return {
    cell: ({ getValue }) => String(getValue() ?? ''),
    enableSorting: false,
  } as Partial<ColumnDef<T>>;
}

/**
 * Custom hook that creates and configures the React Table instance
 */
export function useTableInstance<T>({
  data,
  columns,
  enableRowSelection,
  enableColumnFilters,
  rowSelection,
  onRowSelectionChange,
  onPaginationChange,
  getRowCanExpand,
  hasExpandableRows,
  getRowId,
  onUpdateTableData,
  skipAutoResetPageIndex,
  // State из контроллера
  columnOrder,
  columnSizing,
  columnVisibility,
  paginationState,
  sorting,
  columnFilters,
  // Setters из контроллера
  setColumnOrder,
  setColumnSizing,
  setColumnVisibility,
  setPaginationState,
  setSorting,
  setColumnFilters,
}: UseTableInstanceProps<T>): Table<T> {
  const table = useReactTable<T>({
    data,
    columns,
    columnResizeMode: 'onChange',
    columnResizeDirection: 'ltr',
    autoResetExpanded: !skipAutoResetPageIndex,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    autoResetPageIndex: false, // We handle this manually with useSkipper
    defaultColumn: getDefaultColumn<T>(),
    state: {
      columnOrder: [
        ...(enableRowSelection ? ['select'] : []),
        ...(hasExpandableRows ? ['expander'] : []),
        ...columnOrder,
      ],
      columnSizing,
      columnVisibility,
      pagination: paginationState,
      rowSelection,
      sorting,
      columnFilters,
    },
    meta: {
      updateData: (props: { row: Row<T>; column: Column<T, unknown>; value: unknown }) => {
        skipAutoResetPageIndex();
        onUpdateTableData({
          row: props?.row,
          column: props?.column,
          value: props?.value,
        });
      },
    },
    rowCount: data?.length,
    manualPagination: typeof onPaginationChange === 'function',
    manualSorting: true,
    enableRowSelection,
    enableColumnFilters,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPaginationState,
    onSortingChange: setSorting,
    onRowSelectionChange,
    onColumnFiltersChange: setColumnFilters,
    getRowCanExpand,
    getRowId,
  });

  return table;
}
